/**
 * The «@» of the loan-request composer (#136) — the pure half, kept out of the
 * page so it can be tested without rendering a timeline.
 *
 * The one rule everything here serves: a mention is a PICK from the list the
 * server sent (`mention_candidates`), never a name read back out of the text.
 * Parsing «@علی» out of a message would notify whichever «علی» matched first —
 * two managers can share a name, and a requester can be called anything — and
 * the person it reached would be told, with no way to undo it, about a
 * conversation that was not meant for them. So the text only ever shows the
 * pick; the ids travel beside it.
 */

/**
 * The same letter typed on an Arabic and on a Persian keyboard: «علي» and
 * «علی» must find the same manager. ZWNJ is dropped so «عبدالله» matches
 * however its writer joined it.
 */
export const normalizeForMatch = (text: string) =>
    text
        .replace(/[يى]/g, 'ی')
        .replace(/ك/g, 'ک')
        .replace(/‌/g, '')
        .toLowerCase()
        .trim()

/** Longer than any name; past this the «@» was not a mention. */
const MAX_QUERY = 30

/**
 * The «@…» the caret is in, or null.
 *
 * The «@» must start the text or follow whitespace — `a@b` is an address, not
 * a mention — and the query stops at a newline. Spaces are allowed, because
 * names have them («علی احمدی»); a query that ENDS in a space returns null, so
 * the list closes the moment a pick has been written out as «@نام » and does
 * not reopen on the very name it just inserted.
 */
export function mentionQueryAt(
    text: string,
    caret: number
): { start: number; query: string } | null {
    const before = text.slice(0, caret)
    const start = before.lastIndexOf('@')
    if (start < 0) return null
    if (start > 0 && !/\s/.test(before[start - 1])) return null
    const query = before.slice(start + 1)
    if (query.length > MAX_QUERY || /\n/.test(query) || /\s$/.test(query))
        return null
    return { start, query }
}

/** Everyone whose name contains the query; all of them for a bare «@». */
export function matchCandidates(
    candidates: ILoanDemandMentionCandidate[],
    query: string
) {
    const needle = normalizeForMatch(query)
    if (!needle) return candidates
    return candidates.filter((person) =>
        normalizeForMatch(person.name).includes(needle)
    )
}

/**
 * The draft with «@query» replaced by «@نام », and where the caret goes.
 * The trailing space ends the mention (see `mentionQueryAt`).
 */
export function insertMention(
    text: string,
    start: number,
    caret: number,
    name: string
) {
    const inserted = `@${name} `
    return {
        text: text.slice(0, start) + inserted + text.slice(caret),
        caret: start + inserted.length,
    }
}

/**
 * The picks that still stand. A pick whose «@نام» was deleted from the draft
 * is gone — the writer took it back — and the requester is never named in a
 * note, the one message on the page they must not hear of (the server refuses
 * that too). Nothing is ever ADDED here: text that merely looks like a
 * mention stays text.
 */
export function standingMentions(
    picked: ILoanDemandMentionCandidate[],
    draft: string,
    noting: boolean
) {
    return picked.filter(
        (person) =>
            draft.includes(`@${person.name}`) &&
            !(noting && person.is_requester)
    )
}

/** The draft with this person's «@» taken off — the chip's «×». */
export function unmention(draft: string, name: string) {
    return draft.split(`@${name}`).join(name)
}

/**
 * The comment endpoint's 400 for a pick it will not take: someone who stopped
 * being a manager since the page loaded, or the requester in a note. English
 * on the wire, so the page shows its own sentence.
 */
export function isMentionRefusal(error: unknown) {
    const response = (
        error as {
            response?: { status?: number; data?: { message?: unknown } }
        }
    )?.response
    const message = response?.data?.message
    return (
        response?.status === 400 &&
        typeof message === 'string' &&
        message.includes('mention')
    )
}
